'use client';

import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useApplyCreditBalance } from '@/hooks/use-credit-balance';
import type { CompanySearchParams } from '@/scripts/search-queries';
import { parseSearchExportResponse, type SearchExportOutcome } from '@/lib/search-export';

async function requestSearchExport(
  params: CompanySearchParams,
  rows: number,
): Promise<SearchExportOutcome> {
  const res = await fetch('/api/search/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ params, rows }),
  });
  const body: unknown = await res.json().catch(() => null);
  return parseSearchExportResponse(res.status, body);
}

/**
 * Fetch the rows of a paid Excel export (1,000 credits, up to 10,000 rows,
 * billed by the backend once the rows exist). A successful charge pushes
 * the new balance into the header pill. Refusals come back as outcomes
 * (`insufficient_credits`, `limit_exceeded`, `auth_required`); anything
 * else throws.
 */
export function useSearchExport(): (
  params: CompanySearchParams,
  rows: number,
) => Promise<SearchExportOutcome> {
  const applyCreditBalance = useApplyCreditBalance();
  const { mutateAsync } = useMutation({
    mutationFn: ({ params, rows }: { params: CompanySearchParams; rows: number }) =>
      requestSearchExport(params, rows),
    onSuccess: (outcome) => {
      if (outcome.kind === 'ok') applyCreditBalance(outcome.balance);
    },
  });
  // mutateAsync is stable across renders, so callers can list this in deps.
  return useCallback((params, rows) => mutateAsync({ params, rows }), [mutateAsync]);
}
